/*
 This file is part of GNU Taler
 (C) 2022-2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 * Imports.
 */
import {
  HeadersImpl,
  HttpRawLib,
  HttpRawRequestOptions,
  HttpRawResponse,
  RequestCancelledError,
  RequestTimeoutError,
} from "@gnu-taler/taler-util/http";

/**
 * Implementation of the raw HTTP layer for the browser, using the fetch API.
 */
export const browserRawLib: HttpRawLib = {
  compress,

  async fetch(
    url: string,
    opt: HttpRawRequestOptions,
  ): Promise<HttpRawResponse> {
    const controller = new AbortController();
    let timedOut = false;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let unregisterCancel: (() => void) | undefined;

    const cleanup = (): void => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      if (unregisterCancel !== undefined) {
        unregisterCancel();
        unregisterCancel = undefined;
      }
    };

    if (opt.timeoutMs !== undefined) {
      timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, opt.timeoutMs);
    }
    if (opt.cancellationToken) {
      unregisterCancel = opt.cancellationToken.onCancelled(() => {
        cancelled = true;
        controller.abort();
      });
    }

    let response: Response;
    try {
      response = await globalThis.fetch(url, {
        headers: opt.headers,
        body: opt.body != null ? new Uint8Array(opt.body) : undefined,
        method: opt.method,
        signal: controller.signal,
        redirect: opt.redirect,
      });
    } catch (e) {
      // The abort reason is tracked separately: an AbortError does not say
      // whether we timed out or the caller cancelled.
      if (timedOut) {
        throw new RequestTimeoutError();
      }
      if (cancelled) {
        throw new RequestCancelledError();
      }
      throw e;
    } finally {
      cleanup();
    }

    const headers = new HeadersImpl();
    response.headers.forEach((value, key) => {
      headers.set(key, value);
    });

    return {
      status: response.status,
      headers,
      async bytes() {
        const buf = await response.arrayBuffer();
        return new Uint8Array(buf);
      },
    };
  },
};

export async function compress(
  alg: "gzip" | "deflate" | undefined,
  buf: Uint8Array,
): Promise<Uint8Array> {
  if (!alg) return buf;
  const cs = new CompressionStream(alg);
  const writer = cs.writable.getWriter();
  writer.write(new Uint8Array(buf));
  writer.close();
  const retBuf = await new Response(cs.readable).arrayBuffer();
  return new Uint8Array(retBuf);
}
